import { useState, useEffect, useCallback } from 'react';
import { doc, setDoc, deleteDoc, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { encryptData, decryptData, isEncrypted } from '../utils/encryption';

export function useTimer(user, isOnline, onTimerStop) {
  const [currentTimer, setCurrentTimer] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  // Subscribe to current timer from Firestore (with encryption support)
  useEffect(() => {
    if (!user) {
      setCurrentTimer(null);
      setIsLoading(false);
      return;
    }

    // Load from localStorage first for immediate display (with decryption)
    (async () => {
      try {
        const savedTimer = localStorage.getItem(`currentTimer_${user.uid}`);
        if (savedTimer) {
          if (isEncrypted(savedTimer)) {
            const decrypted = await decryptData(savedTimer, user.uid);
            if (decrypted) setCurrentTimer(decrypted);
          } else {
            // Backward compatibility: unencrypted data
            setCurrentTimer(JSON.parse(savedTimer));
          }
        }
      } catch (error) {
        console.error('Error loading encrypted timer:', error);
      }
    })();

    if (!isOnline) {
      setIsLoading(false);
      return;
    }

    const unsubscribe = onSnapshot(
      doc(db, 'currentTimers', user.uid),
      async (snapshot) => {
        if (snapshot.exists()) {
          const timerData = snapshot.data();
          setCurrentTimer(timerData);

          // Save encrypted to localStorage
          try {
            const encrypted = await encryptData(timerData, user.uid);
            localStorage.setItem(`currentTimer_${user.uid}`, encrypted);
          } catch (error) {
            console.error('Error saving encrypted timer:', error);
          }
        } else {
          setCurrentTimer(null);
          localStorage.removeItem(`currentTimer_${user.uid}`);
        }
        setIsLoading(false);
      },
      (error) => {
        console.error('Timer sync error:', error);
        setIsLoading(false);
      }
    );

    return unsubscribe;
  }, [user, isOnline]);

  // Save timer to localStorage as backup (with encryption)
  useEffect(() => {
    if (user && currentTimer) {
      (async () => {
        try {
          const encrypted = await encryptData(currentTimer, user.uid);
          localStorage.setItem(`currentTimer_${user.uid}`, encrypted);
        } catch (error) {
          console.error('Error saving encrypted timer:', error);
        }
      })();
    } else if (user) {
      localStorage.removeItem(`currentTimer_${user.uid}`);
    }
  }, [currentTimer, user]);

  const saveTimerToFirestore = useCallback(async (timer) => {
    if (!user || !isOnline) return;

    try {
      if (timer) {
        await setDoc(doc(db, 'currentTimers', user.uid), timer);
      } else {
        await deleteDoc(doc(db, 'currentTimers', user.uid));
      }
    } catch (error) {
      console.error('Error saving timer to Firestore:', error);
    }
  }, [user, isOnline]);

  const startTimer = useCallback(async (description, projectId) => {
    const newTimer = {
      id: Date.now(),
      description: description.trim(),
      startTime: Date.now(),
      endTime: null,
      projectId
    };
    setCurrentTimer(newTimer);
    await saveTimerToFirestore(newTimer);
  }, [saveTimerToFirestore]);

  const updateTimer = useCallback(async (description, projectId = null) => {
    if (!currentTimer) return;

    const updatedTimer = {
      ...currentTimer,
      description: description.trim(),
      ...(projectId && { projectId })
    };
    setCurrentTimer(updatedTimer);
    await saveTimerToFirestore(updatedTimer);
  }, [currentTimer, saveTimerToFirestore]);

  const updateTimerStartTime = useCallback(async (newStartTime) => {
    if (!currentTimer) return;
    const timestamp = new Date(newStartTime).getTime();
    if (isNaN(timestamp) || timestamp > Date.now()) return;

    const updatedTimer = {
      ...currentTimer,
      startTime: timestamp
    };
    setCurrentTimer(updatedTimer);
    await saveTimerToFirestore(updatedTimer);
  }, [currentTimer, saveTimerToFirestore]);

  const stopTimer = useCallback(async () => {
    if (!currentTimer) return;

    const completedEntry = {
      ...currentTimer,
      endTime: Date.now()
    };

    setCurrentTimer(null);
    await saveTimerToFirestore(null);

    if (onTimerStop) {
      onTimerStop(completedEntry);
    }

    return completedEntry;
  }, [currentTimer, onTimerStop, saveTimerToFirestore]);

  const clearTimer = useCallback(async () => {
    setCurrentTimer(null);
    await saveTimerToFirestore(null);
  }, [saveTimerToFirestore]);

  return {
    currentTimer,
    isLoading,
    startTimer,
    updateTimer,
    updateTimerStartTime,
    stopTimer,
    clearTimer
  };
}
